import React from 'react';
import PropTypes from 'prop-types';
import round from 'lodash/round';
import { connect } from 'react-redux';
import a from '../../../../../redux/actions';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';

import InlineInput from '../InlineInput';
import units from '../../../../../lib/units';

const velocityUnit = (isMetric, t) => (isMetric ? t('km/h') : t('mph'));

const velocityImprovements = (isMetric, t) => ({
  velocityUnit: velocityUnit(isMetric, t),
  percentReduction: isMetric ? round(1 / units.KM_PER_MI, 1) : 1,
  velocityThreshold: isMetric ? round(units.miToKm(60)) : 60,
});

const PracticeEcoDriving = ({
  t,
  isMetric,
  resultTakeactionPracticeEcoDrivingDispmiles,
  resultTakeactionPracticeEcoDrivingMpg,
  resultTakeactionPracticeEcoDrivingNewmpg,
  resultTakeactionPracticeEcoDrivingGalsaved,
  widgetSource,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I currently drive a total of {{resultTakeactionPracticeEcoDrivingDispmiles}} {{distanceUnit}} per year, of which', { resultTakeactionPracticeEcoDrivingDispmiles, distanceUnit: isMetric ? t('kilometers', { ns: 'units' }) : t('miles', { ns: 'units' }) })}
        <InlineInput
          percent
          maxLength={3}
          apiKey={'input_takeaction_practice_eco_driving_hwpercent'}
        />
        {t('are highway {{distanceUnit}}. The average fuel economy of my vehicles is {{resultTakeactionPracticeEcoDrivingMpg}} {{fuelEfficiencyUnit}}.  Reducing my top cruising highway speed from', {
          distanceUnit: isMetric ? t('kilometers', { ns: 'units' }) : t('miles', { ns: 'units' }),
          resultTakeactionPracticeEcoDrivingMpg,
          fuelEfficiencyUnit: isMetric ? t('L/100km', { ns: 'units' }) : t('mpg', { ns: 'units' }),
        })}
        <InlineInput
          maxLength={3}
          apiKey={'input_takeaction_practice_eco_driving_topspeed'}
        />
        {t('{{velocityUnit}} to', { velocityUnit: velocityUnit(isMetric, t) })}
        <InlineInput
          maxLength={3}
          apiKey={'input_takeaction_practice_eco_driving_lowerspeed'}
        />
        {t('{{velocityUnit}} will improve my fuel economy by {{percentReduction}}% per {{velocityUnit}} reduction over {{velocityThreshold}} {{velocityUnit}}. Reducing all rapid acceleration and braking can improve non-highway fuel economy by 3%. Together these actions will improve my fuel economy to {{resultTakeactionPracticeEcoDrivingNewmpg}} {{fuelEfficiencyUnit}}, saving {{resultTakeactionPracticeEcoDrivingGalsaved}} {{fuelConsumptionUnit}} per year.', {
          fuelEfficiencyUnit: units.fuelEfficiencyUnit(t)(isMetric, false),
          resultTakeactionPracticeEcoDrivingNewmpg,
          resultTakeactionPracticeEcoDrivingGalsaved,
          fuelConsumptionUnit: units.fuelConsumptionUnit(t)(isMetric, false),
          ...velocityImprovements(isMetric, t),
        })}
      </p>
    </div>
    <div className="action-details__facts">
      {t('Most car engines get less and less efficient as speeds increase. Smart speed management levels off emissions on the freeway and helps prevent ticket troubles.')}
    </div>
    {widgetSource !== 'ontario' && <div className="action-details__links">
      <a
        href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BCategory%253D%253DTransportation%255D%255BEligible+Applicants%253D%253DIndividual%255D/false/none/false/1/12"
        target="_blank"
        rel="noopener noreferrer"
      >{t('Find rebates and incentives for this action')}</a>
    </div>}
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('Price of gasoline:')}
          <InlineInput
            currency
            apiKey="input_takeaction_assumption_price_of_gasoline"
          />
          {t('per {{volumeUnit}}', { volumeUnit: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li>
          {t('Average top cruising speed represents')}
          <InlineInput
            percent
            maxLength={3}
            apiKey="input_takeaction_practice_eco_driving_topspercent"
          />
          {t('of highway {{distanceUnit}}.', { distanceUnit: isMetric ? t('kilometers', { ns: 'units' }) : t('miles', { ns: 'units' }) })}
        </li>
      </ul>
    </div>
  </div>
);

PracticeEcoDriving.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  resultTakeactionPracticeEcoDrivingDispmiles: PropTypes.string.isRequired,
  resultTakeactionPracticeEcoDrivingMpg: PropTypes.string.isRequired,
  resultTakeactionPracticeEcoDrivingNewmpg: PropTypes.string.isRequired,
  resultTakeactionPracticeEcoDrivingGalsaved: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionPracticeEcoDrivingDispmiles',
  'resultTakeactionPracticeEcoDrivingMpg',
  'resultTakeactionPracticeEcoDrivingNewmpg',
  'resultTakeactionPracticeEcoDrivingGalsaved',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
});

const mapDispatchToProps = (_dispatch, _ownProps) => ({});

export default wrap(
  withT('practiceEcoDriving'),
  connect(mapStateToProps, mapDispatchToProps)(PracticeEcoDriving),
);
